import { http } from '@/api/httpClient'
import { readHealthcareAccessToken } from '@/auth/healthcareUiStorage'
import { useAuthStore } from '@/auth/sessionStore'
import type { ImportBatchState, ImportRowDraft } from '@/modules/lab/catalog/types'

const BASE = '/api/v1/lab/catalog-pilot'

export type ServerImportBatch = {
  batchId: string
  fileName: string
  batchStatus: string
  totalRows: number
  appliedRows: number
  failedRows: number
  skippedRows: number
  lastAppliedIndex: number
  expiresAt?: string
  rows: ServerImportRow[]
}

export type ServerImportRow = {
  rowNumber: number
  rawCode: string
  rawName: string
  rawCategory: string
  matchStatus: string
  confidence: number
  suggestedMasterId: string | null
  suggestedCode: string | null
  suggestedName: string | null
  resolution: string | null
  applyStatus?: string
  errorMessage?: string | null
  appliedMasterId?: string | null
}

export type ImportApplyChunkResult = {
  batchId: string
  batchStatus: string
  appliedRows: number
  failedRows: number
  skippedRows: number
  pendingRows: number
  complete: boolean
  failures: Array<{ rowNumber: number; rawName: string; errorMessage: string }>
}

export type BulkPreview = {
  scope: string
  category?: string
  totalInScope: number
  wouldEnable: number
  alreadyEnabled: number
  inactiveEnterprise: number
  billingPendingAfterEnable: number
  scopeDescription: string
}

export type BulkApplyResult = {
  enabled: number
  alreadyEnabled: number
  failed: number
  billingPendingCount: number
  warningMessage?: string
}

export type DedupMatch = {
  masterId: string
  code: string
  name: string
  score: number
  basis: string
  category?: string
  hospitalEnabled: boolean
  draft: boolean
  local: boolean
  aliases: string[]
}

export function toImportBatchState(server: ServerImportBatch, columnMapping: Record<string, string>): ImportBatchState {
  return {
    id: server.batchId,
    fileName: server.fileName,
    createdAt: new Date().toISOString(),
    batchStatus: server.batchStatus,
    appliedRows: server.appliedRows,
    failedRows: server.failedRows,
    skippedRows: server.skippedRows,
    columnMapping,
    rows: server.rows.map(toImportRowDraft),
  }
}

function toImportRowDraft(r: ServerImportRow): ImportRowDraft {
  const conf = typeof r.confidence === 'number' ? r.confidence : Number(r.confidence ?? 0)
  return {
    id: `row-${r.rowNumber}`,
    rowNumber: r.rowNumber,
    rawCode: r.rawCode ?? '',
    rawName: r.rawName,
    rawCategory: r.rawCategory ?? '',
    matchStatus: (r.matchStatus as ImportRowDraft['matchStatus']) ?? 'NEW',
    confidence: conf,
    suggestedMasterId: r.suggestedMasterId,
    suggestedCode: r.suggestedCode,
    suggestedName: r.suggestedName,
    resolution: (r.resolution as ImportRowDraft['resolution']) ?? null,
    applyStatus: r.applyStatus,
    errorMessage: r.errorMessage,
  }
}

export function toServerBatch(batch: ImportBatchState): ServerImportBatch {
  return {
    batchId: batch.id,
    fileName: batch.fileName,
    batchStatus: batch.batchStatus ?? 'READY',
    totalRows: batch.rows.length,
    appliedRows: batch.appliedRows ?? 0,
    failedRows: batch.failedRows ?? 0,
    skippedRows: batch.skippedRows ?? 0,
    lastAppliedIndex: 0,
    rows: batch.rows.map((r) => ({
      rowNumber: r.rowNumber,
      rawCode: r.rawCode,
      rawName: r.rawName,
      rawCategory: r.rawCategory,
      matchStatus: r.matchStatus,
      confidence: r.confidence,
      suggestedMasterId: r.suggestedMasterId,
      suggestedCode: r.suggestedCode,
      suggestedName: r.suggestedName,
      resolution: r.resolution,
    })),
  }
}

export async function fetchActiveImportBatch(): Promise<ImportBatchState | null> {
  if (!hasAuthToken()) return null
  try {
    const res = await http.get<ServerImportBatch>(`${BASE}/import/active`)
    if (!res.data?.batchId) return null
    return toImportBatchState(res.data, {})
  } catch {
    return null
  }
}

export async function saveImportBatchToServer(batch: ImportBatchState): Promise<ImportBatchState> {
  const res = await http.post<ServerImportBatch>(`${BASE}/import`, toServerBatch(batch))
  return toImportBatchState(res.data!, batch.columnMapping)
}

function hasAuthToken(): boolean {
  return Boolean(readHealthcareAccessToken() ?? useAuthStore.getState().accessToken)
}

export async function abandonActiveImportBatch(): Promise<void> {
  if (!hasAuthToken()) return
  await http.delete(`${BASE}/import/active`)
}

export async function updateImportRowResolution(
  batchId: string,
  rowNumber: number,
  resolution: string,
): Promise<void> {
  await http.patch(`${BASE}/import/${batchId}/rows/${rowNumber}`, { resolution })
}

export async function applyImportChunk(
  batchId: string,
  chunkSize = 25,
): Promise<ImportApplyChunkResult> {
  const res = await http.post<ImportApplyChunkResult>(
    `${BASE}/import/${batchId}/apply-chunk?chunkSize=${chunkSize}&autoLinkExact=true`,
    {},
  )
  return res.data!
}

export async function applyImportUntilComplete(
  batchId: string,
  onProgress?: (result: ImportApplyChunkResult) => void,
): Promise<ImportApplyChunkResult> {
  let last: ImportApplyChunkResult | null = null
  for (let i = 0; i < 500; i++) {
    last = await applyImportChunk(batchId)
    onProgress?.(last)
    if (last.complete) break
    if (last.pendingRows === 0) break
  }
  return last!
}

export async function previewBulkEnable(params: {
  scope: 'VISIBLE' | 'SELECTED' | 'CATEGORY'
  category?: string
  masterIds: string[]
}): Promise<BulkPreview> {
  const res = await http.post<BulkPreview>(`${BASE}/bulk/preview`, params)
  return res.data!
}

export async function applyBulkEnable(params: {
  scope: 'VISIBLE' | 'SELECTED' | 'CATEGORY'
  category?: string
  masterIds: string[]
  acknowledgeBillingPending: boolean
}): Promise<BulkApplyResult> {
  const res = await http.post<BulkApplyResult>(`${BASE}/bulk/apply`, params)
  return res.data!
}

export async function fetchAllAliases(): Promise<Record<string, string[]>> {
  const res = await http.get<Record<string, string[]>>(`${BASE}/aliases`)
  return res.data ?? {}
}

export async function replaceAliasesOnServer(masterId: string, aliases: string[]): Promise<void> {
  await http.put(`${BASE}/aliases/${masterId}`, aliases)
}

export async function dedupCheckServer(query: {
  name?: string
  shortCode?: string
  aliases?: string[]
}): Promise<DedupMatch[]> {
  const res = await http.post<DedupMatch[]>(`${BASE}/dedup`, query)
  return res.data ?? []
}

export function isBillingPending(billingServiceId: string | null | undefined): boolean {
  if (!billingServiceId?.trim()) return true
  return billingServiceId.trim().toUpperCase() === 'LAB-CATALOG-PENDING'
}
